import { useCallback, useContext, useEffect, useRef, type ReactElement } from 'react'
import { useStore } from 'react-redux'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  LOYALTY_AIRPORT_TRANSFER_HASH,
  LOYALTY_DISCOUNTED_INSURANCE_HASH,
  LOYALTY_EXCLUSIVE_SAVINGS_HASH,
  LOYALTY_HOTEL_UPGRADE_HASH,
  LOYALTY_TOUR_EXP_CREDIT_HASH,
  LOYALTY_TRAVELSHOOT_CREDIT_HASH,
} from 'loyalty/constants/loyaltyRoutes'
import waitForLoyaltyBenefit from 'loyalty/lib/waitForLoyaltyBenefit'
import LandingPageLoyaltyAirportTransferBenefitsModal from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyAirportTransferBenefitsModal'
import LandingPageLoyaltyDiscountedInsuranceBenefitsModal from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyDiscountedInsuranceBenefitsModal'
import LandingPageLoyaltyExclusiveSavingsModal from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyExclusiveSavingsModal'
import LandingPageLoyaltyStayUpgradeBenefitsModal from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyStayUpgradeBenefitsModal'
import LandingPageLoyaltyTourExpCreditBenefitsModal from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyTourExpCreditBenefitsModal'
import LandingPageLoyaltyTravelShootCreditBenefitsModal from 'loyalty/pages/loyaltyUserAccountPage/benefits/LandingPageLoyaltyTravelShootCreditBenefitsModal'
import useOpenModalOnHash from './useOpenModalOnHash'

function useLoyaltyBenefitHashModals() {
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const store = useStore<App.State>()
  const abortRef = useRef(new AbortController())

  useEffect(() => {
    const abort = new AbortController()
    abortRef.current = abort
    return () => abort.abort()
  }, [])

  const openAfterLogin = useCallback(async(benefitType: App.LoyaltyBenefitType, modal: ReactElement) => {
    const signal = abortRef.current.signal
    if (!loggedIn) {
      const result = await showModal<AccountAccessModalResult>(
        <AccountAccessModal initialMode="login" />,
      )
      if (!result?.loggedIn || signal.aborted) return
    }

    const benefit = await waitForLoyaltyBenefit(store, benefitType, signal)
    if (signal.aborted || !benefit) return

    await showModal(modal)
  }, [loggedIn, showModal, store])

  useOpenModalOnHash(LOYALTY_HOTEL_UPGRADE_HASH, useCallback(() => {
    return openAfterLogin('hotel_upgrade', <LandingPageLoyaltyStayUpgradeBenefitsModal />)
  }, [openAfterLogin]))

  useOpenModalOnHash(LOYALTY_AIRPORT_TRANSFER_HASH, useCallback(() => {
    return openAfterLogin('airport_transfer', <LandingPageLoyaltyAirportTransferBenefitsModal />)
  }, [openAfterLogin]))

  useOpenModalOnHash(LOYALTY_TOUR_EXP_CREDIT_HASH, useCallback(() => {
    return openAfterLogin('tour_exp_credit', <LandingPageLoyaltyTourExpCreditBenefitsModal />)
  }, [openAfterLogin]))

  useOpenModalOnHash(LOYALTY_TRAVELSHOOT_CREDIT_HASH, useCallback(() => {
    return openAfterLogin('travelshoot_credit', <LandingPageLoyaltyTravelShootCreditBenefitsModal />)
  }, [openAfterLogin]))

  useOpenModalOnHash(LOYALTY_DISCOUNTED_INSURANCE_HASH, useCallback(() => {
    return openAfterLogin('discounted_insurance', <LandingPageLoyaltyDiscountedInsuranceBenefitsModal />)
  }, [openAfterLogin]))

  useOpenModalOnHash(LOYALTY_EXCLUSIVE_SAVINGS_HASH, useCallback(() => {
    return openAfterLogin('family_and_friends_access', <LandingPageLoyaltyExclusiveSavingsModal />)
  }, [openAfterLogin]))
}

export default useLoyaltyBenefitHashModals
